import { useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { GridApi, RowDoubleClickedEvent } from '@ag-grid-community/core';
import { RotateCcw, Building2, Shield, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import AGDataGrid from '@/components/ag-data-grid';
import Container from '@/components/container';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useGrid } from '@/contexts/grid-context';
import { useDomainStats } from '@/hooks/use-dashboard-stats';
import { useDomains } from '@/hooks/use-domains';
import { usePersistedState } from '@/hooks/use-persisted-state';
import type { Domain } from '@/types/domain';
import { domainColumnDefs } from './components/domain-grid-columns';

function DomainStatCard({
  title,
  icon: Icon,
  active,
  inactive,
  pendingCreate,
  total,
  className,
}: {
  title: string;
  icon: React.ElementType;
  active: number;
  inactive: number;
  pendingCreate: number;
  total: number;
  className?: string;
}) {
  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="size-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div>
            <div className="text-2xl font-bold">{total}</div>
            <p className="text-xs text-muted-foreground">Total domains</p>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <div className="text-sm font-medium text-green-600">{active}</div>
              <p className="text-xs text-muted-foreground">Active</p>
            </div>
            <div>
              <div className="text-sm font-medium text-amber-600">{inactive}</div>
              <p className="text-xs text-muted-foreground">Inactive</p>
            </div>
            <div>
              <div className="text-sm font-medium text-blue-600">{pendingCreate}</div>
              <p className="text-xs text-muted-foreground">Pending</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

type DomainTld = 'bank' | 'insurance';
type DomainStatus = 'live' | 'parked' | 'redirect_in_zone' | 'redirect_out_of_zone' | 'error' | 'timeout';

const DOMAIN_TLDS: DomainTld[] = ['bank', 'insurance'];
const DOMAIN_STATUSES: DomainStatus[] = ['live', 'parked', 'redirect_in_zone', 'redirect_out_of_zone', 'error', 'timeout'];

const DEFAULT_TLDS = DOMAIN_TLDS;
const DEFAULT_STATUSES = DOMAIN_STATUSES;

function DomainsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTlds, setSelectedTlds] = usePersistedState<DomainTld[]>('domains-selected-tlds', DEFAULT_TLDS);
  const [selectedStatuses, setSelectedStatuses] = usePersistedState<DomainStatus[]>('domains-selected-statuses', DEFAULT_STATUSES);
  const [isFiltersOpen, setIsFiltersOpen] = usePersistedState('domains-filters-open', true);
  const gridApiRef = useRef<GridApi<Domain> | null>(null);
  const { registerGridApi } = useGrid();
  const navigate = useNavigate();

  const { data: domains, isLoading: domainsLoading, error: domainsError } = useDomains();
  const { data: domainStats, error: statsError } = useDomainStats();

  // Apply filters to domain data
  const filteredDomains = useMemo(() => {
    if (!domains)
      return [];

    return domains.filter((domain) => {
      // TLD filter
      const domainTld = domain.name.endsWith('.bank') ? 'bank' : 'insurance';
      if (!selectedTlds.includes(domainTld))
        return false;

      // Status filter (from monitor_data)
      const latestStatus = domain.monitor_data?.at(-1)?.status;
      if (latestStatus && !selectedStatuses.includes(latestStatus as DomainStatus)) {
        return false;
      }

      return true;
    });
  }, [domains, selectedTlds, selectedStatuses]);

  // Show error toasts if the queries fail
  if (domainsError instanceof Error) {
    toast.error(domainsError.message);
  }
  if (statsError instanceof Error) {
    toast.error(statsError.message);
  }

  const handleRowDoubleClick = (event: RowDoubleClickedEvent<Domain>) => {
    if (event.data) {
      navigate(`/app/domains/${event.data.name}`);
    }
  };

  const handleGridReady = (params: { api: GridApi<Domain> }) => {
    gridApiRef.current = params.api;
    registerGridApi(params.api);
  };

  const clearFilters = () => {
    if (gridApiRef.current) {
      gridApiRef.current.setFilterModel(null);
      setSearchQuery('');
      setSelectedTlds(DEFAULT_TLDS);
      setSelectedStatuses(DEFAULT_STATUSES);
    }
  };

  return (
    <Container className="pb-12">
      <h1 className="mb-6 text-3xl font-bold">Domains</h1>
      <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
        <DomainStatCard
          title=".Bank"
          icon={Building2}
          active={domainStats?.bank.active ?? 0}
          inactive={domainStats?.bank.inactive ?? 0}
          pendingCreate={domainStats?.bank.pendingCreate ?? 0}
          total={(domainStats?.bank.active ?? 0) + (domainStats?.bank.inactive ?? 0)}
        />
        <DomainStatCard
          title=".Insurance"
          icon={Shield}
          active={domainStats?.insurance.active ?? 0}
          inactive={domainStats?.insurance.inactive ?? 0}
          pendingCreate={domainStats?.insurance.pendingCreate ?? 0}
          total={(domainStats?.insurance.active ?? 0) + (domainStats?.insurance.inactive ?? 0)}
        />
      </div>
      <div className="mb-6 flex gap-4">
        <div className="flex-1">
          <Input
            type="search"
            placeholder="Search domains..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Button
          variant="outline"
          onClick={clearFilters}
          className="gap-2"
        >
          <RotateCcw className="size-4" />
          Clear Filters
        </Button>
      </div>

      <Collapsible
        open={isFiltersOpen}
        onOpenChange={setIsFiltersOpen}
        className="mb-6 space-y-2"
      >
        <div className="flex items-center gap-2">
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-2">
              <ChevronDown className={`size-4 transition-transform ${isFiltersOpen ? 'rotate-180' : ''}`} />
              Filters
            </Button>
          </CollapsibleTrigger>
          <div className="text-sm text-muted-foreground">
            {selectedTlds.length}
            {' '}
            TLDs,
            {' '}
            {selectedStatuses.length}
            {' '}
            statuses
          </div>
        </div>

        <CollapsibleContent className="space-y-4">
          <div className="flex flex-wrap items-start gap-8">
            {/* TLD Filter */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">TLD</Label>
                <Checkbox
                  checked={selectedTlds.length === DOMAIN_TLDS.length}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSelectedTlds([...DOMAIN_TLDS]);
                    } else {
                      setSelectedTlds([]);
                    }
                  }}
                />
                <Label className="text-xs text-muted-foreground">All</Label>
              </div>
              <div className="grid grid-cols-1 gap-y-2">
                {DOMAIN_TLDS.map((tld) => (
                  <div key={tld} className="flex items-center gap-2">
                    <Checkbox
                      id={`tld-${tld}`}
                      checked={selectedTlds.includes(tld)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedTlds([...selectedTlds, tld]);
                        } else {
                          setSelectedTlds(selectedTlds.filter((t) => t !== tld));
                        }
                      }}
                    />
                    <Label htmlFor={`tld-${tld}`} className="text-sm capitalize">
                      .
                      {tld === 'bank' ? 'Bank' : 'Insurance'}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Status Filter */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Status</Label>
                <Checkbox
                  checked={selectedStatuses.length === DOMAIN_STATUSES.length}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSelectedStatuses([...DOMAIN_STATUSES]);
                    } else {
                      setSelectedStatuses([]);
                    }
                  }}
                />
                <Label className="text-xs text-muted-foreground">All</Label>
              </div>
              <div className="grid grid-cols-2 gap-x-8 gap-y-2">
                {DOMAIN_STATUSES.map((status) => (
                  <div key={status} className="flex items-center gap-2">
                    <Checkbox
                      id={`status-${status}`}
                      checked={selectedStatuses.includes(status)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedStatuses([...selectedStatuses, status]);
                        } else {
                          setSelectedStatuses(selectedStatuses.filter((s) => s !== status));
                        }
                      }}
                    />
                    <Label htmlFor={`status-${status}`} className="text-sm capitalize">{status.replaceAll('_', ' ')}</Label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      <div className={domainsLoading ? 'opacity-50' : ''}>
        <AGDataGrid
          columnDefs={domainColumnDefs}
          rowData={filteredDomains}
          quickFilterText={searchQuery}
          onRowDoubleClicked={handleRowDoubleClick}
          onGridReady={handleGridReady}
          overlayLoadingTemplate={
            domainsLoading ? '<span class="ag-overlay-loading-center">Loading...</span>' : undefined
          }
          overlayNoRowsTemplate={
            domainsLoading ? '' : '<span class="ag-overlay-no-rows-center">No domains found</span>'
          }
        />
      </div>
    </Container>
  );
}

export default DomainsPage;
